/**
 * FoodsPage — "מאגר המזונות" (nutrition module, Phase 1).
 *
 * A searchable catalog merging the global NiV-curated food list with this
 * clinic's own custom foods. ALL nutrition values are PER 100 GRAMS.
 *
 * Read-only for global foods; clinic foods can be edited/deleted by the coach.
 * A newly created clinic food is also raised to CliniKit for review for the
 * shared catalog (handled server-side by createCustomFood).
 *
 * Validation mirrors src/utils/foodsNormalize.mjs for instant Hebrew feedback —
 * the server normalizer stays the single source of truth.
 *
 * Error codes: CK-10010 (search), CK-10011 (create), CK-10012 (update),
 *              CK-10013 (delete), CK-10014 (load more page).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  BowlFood,
  CaretDown,
  Check,
  FunnelSimple,
  MagnifyingGlass,
  Note,
  PencilSimple,
  Plus,
  Storefront,
  Trash,
  X,
} from "@phosphor-icons/react";

import {
  selectClinicId,
  selectIsNutritionClinic,
  selectTherapistId,
  selectTherapistName,
} from "../../../store/slices/authSlice";
import {
  useCreateCustomFoodMutation,
  useDeleteCustomFoodMutation,
  useSearchFoodsQuery,
  useUpdateCustomFoodMutation,
} from "../../../store/services/nutritionFoodsApi";
import {
  CURATED_UNITS,
  MAX_CALORIES,
  MAX_MACRO_SUM,
  MAX_UNIT_GRAMS,
  OPTIONAL_NUMERIC_FIELDS,
} from "../../../utils/foodsNormalize.mjs";
import { PageHeader } from "../../../components/shared";
import EmptyDraw from "../../../components/ui/EmptyDraw";
import ModalCloseButton from "../../../components/ui/ModalCloseButton";
import ConfirmDialog from "../../../components/ui/ConfirmDialog";
import CustomClinikitActionButton from "../../../components/ui/CustomClinikitActionButton";
import Input from "../../../components/ui/Input";
import { useToast } from "../../../contexts/ToastContext";
import "./FoodsPage.css";
import LoaderLoop from "../../../components/shared/loader/LoaderLoop";
import ContentLoader from "../../../components/shared/ContentLoader";

// ============================================================================
// Constants
// ============================================================================

const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 350;
/** Placeholder rows shown while the first page of a query loads. */

/**
 * Hebrew label + display unit for every nutrient we may show.
 * Units are per 100g. Minerals/most B vitamins are milligrams; the
 * micro-dosed vitamins (A, D, K, B9, B12) are micrograms.
 */
const NUTRIENT_LABELS = {
  calories: { label: "קלוריות", unit: 'קק"ל' },
  protein: { label: "חלבון", unit: "גרם" },
  carbohydrates: { label: "פחמימות", unit: "גרם" },
  fat: { label: "שומן", unit: "גרם" },
  // macro detail
  saturatedFat: { label: "שומן רווי", unit: "גרם" },
  sugar: { label: "סוכר", unit: "גרם" },
  dietaryFiber: { label: "סיבים תזונתיים", unit: "גרם" },
  sodium: { label: "נתרן", unit: 'מ"ג' },
  cholesterol: { label: "כולסטרול", unit: 'מ"ג' },
  water: { label: "מים", unit: "גרם" },
  // minerals
  calcium: { label: "סידן", unit: 'מ"ג' },
  iron: { label: "ברזל", unit: 'מ"ג' },
  potassium: { label: "אשלגן", unit: 'מ"ג' },
  magnesium: { label: "מגנזיום", unit: 'מ"ג' },
  phosphorus: { label: "זרחן", unit: 'מ"ג' },
  zinc: { label: "אבץ", unit: 'מ"ג' },
  // vitamins
  vitaminA: { label: "ויטמין A", unit: 'מק"ג' },
  vitaminC: { label: "ויטמין C", unit: 'מ"ג' },
  vitaminD: { label: "ויטמין D", unit: 'מק"ג' },
  vitaminE: { label: "ויטמין E", unit: 'מ"ג' },
  vitaminK: { label: "ויטמין K", unit: 'מק"ג' },
  vitaminB1: { label: "ויטמין B1 (תיאמין)", unit: 'מ"ג' },
  vitaminB2: { label: "ויטמין B2 (ריבופלבין)", unit: 'מ"ג' },
  vitaminB3: { label: "ויטמין B3 (ניאצין)", unit: 'מ"ג' },
  vitaminB5: { label: "ויטמין B5 (פנטותנית)", unit: 'מ"ג' },
  vitaminB6: { label: "ויטמין B6", unit: 'מ"ג' },
  vitaminB9: { label: "ויטמין B9 (חומצה פולית)", unit: 'מק"ג' },
  vitaminB12: { label: "ויטמין B12", unit: 'מק"ג' },
  // fatty acids
  omega3: { label: "אומגה 3", unit: "גרם" },
  omega6: { label: "אומגה 6", unit: "גרם" },
  monounsaturatedFat: { label: "שומן חד בלתי רווי", unit: "גרם" },
  polyunsaturatedFat: { label: "שומן רב בלתי רווי", unit: "גרם" },
  oleicAcid: { label: "חומצה אולאית", unit: "גרם" },
};

/**
 * The optional micronutrients we render, in the normalizer's display order.
 * Filtered to the fields that have a Hebrew label, so a new field added to the
 * normalizer never renders as an unlabeled row.
 */
const OPTIONAL_FIELDS = OPTIONAL_NUMERIC_FIELDS.filter(
  (field) => !!NUTRIENT_LABELS[field]
);

/** Source tabs — "הכל" first, as the design language requires. */
const SOURCE_TABS = [
  { id: "all", label: "הכל", icon: BowlFood },
  { id: "global", label: "מאגר כללי", icon: Storefront },
  { id: "clinic", label: "מזונות המרפאה", icon: Note },
];

/** Macro range filters (min/max pairs the API understands). */
const RANGE_FILTERS = [
  { key: "Calories", label: "קלוריות", unit: 'קק"ל' },
  { key: "Protein", label: "חלבון", unit: "גרם" },
  { key: "Carbohydrates", label: "פחמימות", unit: "גרם" },
  { key: "Fat", label: "שומן", unit: "גרם" },
];

const EMPTY_RANGES = RANGE_FILTERS.reduce(
  (acc, { key }) => ({ ...acc, [`min${key}`]: "", [`max${key}`]: "" }),
  {}
);

const EMPTY_FORM = {
  foodName: "",
  calories: "",
  protein: "",
  carbohydrates: "",
  fat: "",
  units: [],
  extras: {},
};

const CURATED_UNIT_LIST = Array.from(CURATED_UNITS);

// ============================================================================
// Helpers (presentation only — the math contract lives in foodsNormalize)
// ============================================================================

/** Round for display without trailing ".0". */
const fmt = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  const num = Number(value);
  if (!Number.isFinite(num)) return "—";
  return String(Math.round(num * 10) / 10);
};

/** "" -> null, otherwise a finite number or NaN for invalid input. */
const parseField = (raw) => {
  if (raw === "" || raw === null || raw === undefined) return null;
  const num = Number(String(raw).trim());
  return Number.isFinite(num) ? num : NaN;
};

/**
 * Client-side mirror of normalizeFood's rules, with Hebrew messages.
 * Returns a { field: message } map — empty means valid.
 */
const validateFoodForm = (form) => {
  const errors = {};

  if (!form.foodName || !form.foodName.trim()) {
    errors.foodName = "יש להזין שם מזון";
  }

  const calories = parseField(form.calories);
  if (calories === null || Number.isNaN(calories)) {
    errors.calories = "יש להזין מספר קלוריות";
  } else if (calories < 0 || calories > MAX_CALORIES) {
    errors.calories = `ערך הקלוריות חייב להיות בין 0 ל-${MAX_CALORIES}`;
  }

  let macroSum = 0;
  ["protein", "carbohydrates", "fat"].forEach((field) => {
    const value = parseField(form[field]);
    if (value === null || Number.isNaN(value)) {
      errors[field] = `יש להזין ערך ${NUTRIENT_LABELS[field].label}`;
      return;
    }
    if (value < 0) {
      errors[field] = "הערך לא יכול להיות שלילי";
      return;
    }
    macroSum += value;
  });

  if (!errors.protein && !errors.carbohydrates && !errors.fat && macroSum > MAX_MACRO_SUM) {
    errors.macroSum = `סכום החלבון, הפחמימות והשומן (${
      Math.round(macroSum * 10) / 10
    } גרם) גדול מהמותר ל-100 גרם מזון`;
  }

  form.units.forEach((row, index) => {
    if (!row.unit && !row.grams) return;
    if (!row.unit || !CURATED_UNITS.has(row.unit)) {
      errors[`unit_${index}`] = "יש לבחור יחידת מידה מהרשימה";
      return;
    }
    const grams = parseField(row.grams);
    if (grams === null || Number.isNaN(grams) || grams <= 0 || grams > MAX_UNIT_GRAMS) {
      errors[`unit_${index}`] = `משקל היחידה חייב להיות בין 0 ל-${MAX_UNIT_GRAMS} גרם`;
    }
  });

  const unitNames = form.units.map((row) => row.unit).filter(Boolean);
  if (new Set(unitNames).size !== unitNames.length) {
    errors.unitsDuplicate = "כל יחידת מידה יכולה להופיע פעם אחת בלבד";
  }

  OPTIONAL_FIELDS.forEach((field) => {
    const raw = form.extras[field];
    if (raw === undefined || raw === "") return;
    const value = parseField(raw);
    if (Number.isNaN(value) || value < 0) {
      errors[`extra_${field}`] = "ערך לא תקין";
    }
  });

  return errors;
};

/** Build the payload the API/normalizer expects out of the form state. */
const buildFoodData = (form) => {
  const sizeOptions = {};
  form.units.forEach((row) => {
    if (!row.unit) return;
    const grams = parseField(row.grams);
    if (grams === null || Number.isNaN(grams)) return;
    sizeOptions[row.unit] = grams;
  });

  const payload = {
    foodName: form.foodName.trim(),
    calories: parseField(form.calories),
    protein: parseField(form.protein),
    carbohydrates: parseField(form.carbohydrates),
    fat: parseField(form.fat),
    sizeOptions,
  };

  OPTIONAL_FIELDS.forEach((field) => {
    const raw = form.extras[field];
    if (raw === undefined || raw === "") return;
    const value = parseField(raw);
    if (value === null || Number.isNaN(value)) return;
    payload[field] = value;
  });

  return payload;
};

/** Prefill the form from an existing clinic food. */
const formFromFood = (food) => ({
  foodName: food.foodName || "",
  calories: food.calories ?? "",
  protein: food.protein ?? "",
  carbohydrates: food.carbohydrates ?? "",
  fat: food.fat ?? "",
  units: Object.entries(food.sizeOptions || {}).map(([unit, grams]) => ({
    unit,
    grams: String(grams),
  })),
  extras: OPTIONAL_FIELDS.reduce((acc, field) => {
    if (food[field] === undefined || food[field] === null) return acc;
    return { ...acc, [field]: String(food[field]) };
  }, {}),
});

// ============================================================================
// Page
// ============================================================================

const FoodsPage = () => {
  const clinicId = useSelector(selectClinicId);
  const therapistId = useSelector(selectTherapistId);
  const therapistName = useSelector(selectTherapistName);
  const isNutritionClinic = useSelector(selectIsNutritionClinic);
  const { showSuccess, showError } = useToast();

  // ─── Search (name-prefix → debounced) ───
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  useEffect(() => {
    const timer = setTimeout(
      () => setSearchQuery(searchInput.trim()),
      SEARCH_DEBOUNCE_MS
    );
    return () => clearTimeout(timer);
  }, [searchInput]);

  // ─── Filters ───
  const [source, setSource] = useState("all");
  const [ranges, setRanges] = useState(EMPTY_RANGES);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const filters = useMemo(() => {
    const next = {};
    if (source !== "all") next.source = source;
    Object.entries(ranges).forEach(([key, raw]) => {
      const value = parseField(raw);
      if (value === null || Number.isNaN(value)) return;
      next[key] = value;
    });
    return next;
  }, [source, ranges]);

  const activeRangeCount = useMemo(
    () => Object.values(ranges).filter((value) => value !== "").length,
    [ranges]
  );

  // ─── Pagination (accumulate pages; reset on any query/filter change) ───
  const [cursor, setCursor] = useState(null);
  const [foods, setFoods] = useState([]);
  const queryKey = useMemo(
    () => JSON.stringify({ searchQuery, filters }),
    [searchQuery, filters]
  );
  const lastQueryKeyRef = useRef(queryKey);

  // On a query/filter change only the cursor resets — the rows stay mounted
  // (dimmed) until the new page lands, so the list never flashes empty.
  useEffect(() => {
    if (lastQueryKeyRef.current === queryKey) return;
    lastQueryKeyRef.current = queryKey;
    setCursor(null);
  }, [queryKey]);

  // `currentData` (not `data`): it is undefined while a NEW query is in
  // flight, so a stale page can never be mistaken for the fresh one.
  const {
    currentData: data,
    isLoading,
    isFetching,
    error: searchError,
    refetch,
  } = useSearchFoodsQuery(
    { clinicId, searchQuery, filters, cursor, pageSize: PAGE_SIZE },
    { skip: !clinicId || !isNutritionClinic }
  );

  useEffect(() => {
    if (!data?.foods) return;
    setFoods((prev) => {
      if (cursor === null) return data.foods;
      const seen = new Set(prev.map((food) => food.id));
      return [...prev, ...data.foods.filter((food) => !seen.has(food.id))];
    });
  }, [data, cursor]);

  useEffect(() => {
    if (!searchError) return;
    console.error("[CK-10010] Error loading foods catalog:", {
      errorCode: "CK-10010",
      errorMessage: searchError?.message || "unknown",
      clinicId,
      hasQuery: !!searchQuery,
      timestamp: new Date().toISOString(),
    });
  }, [searchError, clinicId, searchQuery]);

  const handleLoadMore = useCallback(() => {
    if (!data?.hasMore || !data?.nextCursor || isFetching) return;
    try {
      setCursor(data.nextCursor);
    } catch (error) {
      console.error("[CK-10014] Error loading next foods page:", {
        errorCode: "CK-10014",
        errorMessage: error.message,
        clinicId,
        timestamp: new Date().toISOString(),
      });
      showError("טעינת המזונות הנוספים נכשלה, נסו שוב");
    }
  }, [data, isFetching, clinicId, showError]);

  const resetPagination = useCallback(() => {
    setCursor(null);
    setFoods([]);
  }, []);

  const handleClearFilters = useCallback(() => {
    setRanges(EMPTY_RANGES);
  }, []);

  // ─── Mutations ───
  const [createCustomFood, { isLoading: isCreating }] = useCreateCustomFoodMutation();
  const [updateCustomFood, { isLoading: isUpdating }] = useUpdateCustomFoodMutation();
  const [deleteCustomFood, { isLoading: isDeleting }] = useDeleteCustomFoodMutation();

  // ─── Modals ───
  const [editorFood, setEditorFood] = useState(null); // null = closed
  const [editorMode, setEditorMode] = useState("create");
  const [detailFood, setDetailFood] = useState(null);
  const [foodToDelete, setFoodToDelete] = useState(null);

  const openCreate = useCallback(() => {
    setEditorMode("create");
    setEditorFood(EMPTY_FORM);
  }, []);

  const openEdit = useCallback((food) => {
    setEditorMode("edit");
    setEditorFood({ ...formFromFood(food), id: food.id });
  }, []);

  const handleSubmitFood = useCallback(
    async (form) => {
      const foodData = buildFoodData(form);
      if (editorMode === "edit") {
        try {
          await updateCustomFood({
            clinicId,
            foodId: form.id,
            foodData,
            therapistId,
            therapistName,
          }).unwrap();
          showSuccess("המזון עודכן בהצלחה");
          setEditorFood(null);
          resetPagination();
        } catch (error) {
          console.error("[CK-10012] Error updating custom food:", {
            errorCode: "CK-10012",
            errorMessage: error?.message || error?.data?.message || "unknown",
            clinicId,
            foodId: form.id,
            timestamp: new Date().toISOString(),
          });
          showError(error?.message || error?.data?.message || "עדכון המזון נכשל, נסו שוב");
        }
        return;
      }

      try {
        await createCustomFood({
          clinicId,
          foodData,
          therapistId,
          therapistName,
        }).unwrap();
        showSuccess("המזון נוסף למאגר המרפאה ונשלח לבדיקת CliniKit");
        setEditorFood(null);
        resetPagination();
      } catch (error) {
        console.error("[CK-10011] Error creating custom food:", {
          errorCode: "CK-10011",
          errorMessage: error?.message || error?.data?.message || "unknown",
          clinicId,
          timestamp: new Date().toISOString(),
        });
        showError(error?.message || error?.data?.message || "הוספת המזון נכשלה, נסו שוב");
      }
    },
    [
      editorMode,
      clinicId,
      therapistId,
      therapistName,
      createCustomFood,
      updateCustomFood,
      resetPagination,
      showSuccess,
      showError,
    ]
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!foodToDelete) return;
    try {
      await deleteCustomFood({
        clinicId,
        foodId: foodToDelete.id,
        foodName: foodToDelete.foodName,
        therapistId,
        therapistName,
      }).unwrap();
      showSuccess("המזון נמחק ממאגר המרפאה");
      setFoodToDelete(null);
      setDetailFood(null);
      resetPagination();
    } catch (error) {
      console.error("[CK-10013] Error deleting custom food:", {
        errorCode: "CK-10013",
        errorMessage: error?.message || error?.data?.message || "unknown",
        clinicId,
        foodId: foodToDelete.id,
        timestamp: new Date().toISOString(),
      });
      showError(error?.message || error?.data?.message || "מחיקת המזון נכשלה, נסו שוב");
    }
  }, [
    foodToDelete,
    clinicId,
    therapistId,
    therapistName,
    deleteCustomFood,
    resetPagination,
    showSuccess,
    showError,
  ]);

  // Defensive gate — routing also guards this page.
  if (!isNutritionClinic) return null;

  // First page of a fresh query is loading. With no rows yet → skeleton;
  // with previous rows → keep them, dimmed. Never the empty state.
  const isRefreshing = isFetching && cursor === null;
  const showInitialLoader = (isLoading || isRefreshing) && foods.length === 0;
  const isEmpty =
    !showInitialLoader && !isFetching && !searchError && foods.length === 0;
  const hasFiltersApplied = !!searchQuery || source !== "all" || activeRangeCount > 0;

  return (
    <div className="nfp-page">
      <PageHeader
        icon={BowlFood}
        title="מאגר המזונות"
        subtitle="כל הערכים התזונתיים מוצגים ל-100 גרם"
        actions={
          <div className="nfp-header-actions">
            <CustomClinikitActionButton
              icon={<Plus size={16} weight="bold" />}
              onClick={openCreate}
            >
              הוספת מזון
            </CustomClinikitActionButton>
          </div>
        }
      />

      <div className="nfp-card">
        {/* Source tabs — full-width, flush, elevated */}
        <div className="nfp-tabs" role="tablist" aria-label="מקור המזונות">
          {SOURCE_TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = source === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={`nfp-tab ${isActive ? "active" : ""}`}
                onClick={() => setSource(tab.id)}
              >
                <Icon size={15} weight={isActive ? "fill" : "regular"} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        <div className="nfp-body">
          {/* Toolbar */}
          <div className="nfp-toolbar">
            <div className="nfp-search-box">
              <MagnifyingGlass size={16} weight="bold" />
              <input
                type="text"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                placeholder="חיפוש מזון לפי שם..."
                aria-label="חיפוש מזון"
              />
              {searchInput && (
                <button
                  type="button"
                  className="nfp-search-clear"
                  onClick={() => setSearchInput("")}
                  aria-label="ניקוי חיפוש"
                >
                  <X size={14} weight="bold" />
                </button>
              )}
            </div>

            <button
              type="button"
              className={`nfp-filter-toggle ${filtersOpen || activeRangeCount > 0 ? "active" : ""}`}
              onClick={() => setFiltersOpen((open) => !open)}
              aria-expanded={filtersOpen}
            >
              <FunnelSimple size={15} weight="bold" />
              <span>סינון לפי ערכים</span>
              {activeRangeCount > 0 && (
                <span className="nfp-filter-count">{activeRangeCount}</span>
              )}
              <CaretDown
                size={12}
                weight="bold"
                className={`nfp-caret ${filtersOpen ? "open" : ""}`}
              />
            </button>
          </div>

          {filtersOpen && (
            <div className="nfp-filters-panel">
              <div className="nfp-ranges-grid">
                {RANGE_FILTERS.map(({ key, label, unit }) => (
                  <div className="nfp-range-group" key={key}>
                    <span className="nfp-range-label">
                      {label} <span className="nfp-range-unit">({unit} ל-100 גרם)</span>
                    </span>
                    <div className="nfp-range-inputs">
                      <input
                        type="number"
                        min="0"
                        className="nfp-num-input"
                        value={ranges[`min${key}`]}
                        onChange={(event) =>
                          setRanges((prev) => ({
                            ...prev,
                            [`min${key}`]: event.target.value,
                          }))
                        }
                        placeholder="מינימום"
                        aria-label={`${label} מינימום`}
                      />
                      <span className="nfp-range-sep">–</span>
                      <input
                        type="number"
                        min="0"
                        className="nfp-num-input"
                        value={ranges[`max${key}`]}
                        onChange={(event) =>
                          setRanges((prev) => ({
                            ...prev,
                            [`max${key}`]: event.target.value,
                          }))
                        }
                        placeholder="מקסימום"
                        aria-label={`${label} מקסימום`}
                      />
                    </div>
                  </div>
                ))}
              </div>
              {activeRangeCount > 0 && (
                <button type="button" className="nfp-clear-filters" onClick={handleClearFilters}>
                  <X size={12} weight="bold" />
                  <span>ניקוי הסינון</span>
                </button>
              )}
            </div>
          )}

          {/* Results */}
          {showInitialLoader ? (
            <ContentLoader fill message="טוען מזונות..." />
          ) : searchError ? (
            <div className="nfp-empty">
              <EmptyDraw variant="search" />
              <p>טעינת מאגר המזונות נכשלה</p>
              <button type="button" className="nfp-secondary-btn" onClick={() => refetch()}>
                נסו שוב
              </button>
            </div>
          ) : isEmpty ? (
            <div className="nfp-empty">
              <EmptyDraw variant={hasFiltersApplied ? "search" : "template"} />
              <p>
                {hasFiltersApplied
                  ? "לא נמצאו מזונות התואמים לחיפוש"
                  : "מאגר המזונות ריק כרגע"}
              </p>
              <button type="button" className="nfp-secondary-btn" onClick={openCreate}>
                <Plus size={16} weight="bold" />
                <span>הוספת מזון למרפאה</span>
              </button>
            </div>
          ) : (
            <>
              <div
                className={`nfp-table-wrap ${isRefreshing ? "is-refreshing" : ""}`}
                aria-busy={isRefreshing}
              >
                <table className="nfp-table">
                  <caption className="nfp-table-caption">
                    כל הערכים ל-100 גרם מזון
                  </caption>
                  <thead>
                    <tr>
                      <th scope="col">מאכל</th>
                      <th scope="col">קלוריות</th>
                      <th scope="col">פחמימות</th>
                      <th scope="col">חלבון</th>
                      <th scope="col">שומן</th>
                      <th scope="col" className="nfp-th-actions">
                        <span className="nfp-sr-only">פעולות</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {foods.map((food) => {
                      const isClinicFood = food.source === "clinic";
                      return (
                        <tr
                          key={food.id}
                          className={`nfp-row ${isClinicFood ? "clinic" : ""}`}
                          role="button"
                          tabIndex={0}
                          onClick={() => setDetailFood(food)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              setDetailFood(food);
                            }
                          }}
                        >
                          <td data-label="מאכל" className="nfp-cell-name">
                            <span className="nfp-food-name">{food.foodName}</span>
                            {isClinicFood && (
                              <span className="nfp-badge nfp-badge-clinic">מזון המרפאה</span>
                            )}
                          </td>
                          <td data-label="קלוריות (ל-100 גרם)">
                            <span className="nfp-value-cell">
                              <span className="nfp-value">{fmt(food.calories)}</span>
                              <span className="nfp-value-unit">קק"ל</span>
                            </span>
                          </td>
                          <td data-label="פחמימות (ל-100 גרם)">
                            <span className="nfp-value-cell">
                              <span className="nfp-value">{fmt(food.carbohydrates)}</span>
                              <span className="nfp-value-unit">גרם</span>
                            </span>
                          </td>
                          <td data-label="חלבון (ל-100 גרם)">
                            <span className="nfp-value-cell">
                              <span className="nfp-value">{fmt(food.protein)}</span>
                              <span className="nfp-value-unit">גרם</span>
                            </span>
                          </td>
                          <td data-label="שומן (ל-100 גרם)">
                            <span className="nfp-value-cell">
                              <span className="nfp-value">{fmt(food.fat)}</span>
                              <span className="nfp-value-unit">גרם</span>
                            </span>
                          </td>
                          <td className="nfp-cell-actions">
                            {isClinicFood && (
                              <div className="nfp-actions">
                                <button
                                  type="button"
                                  className="nfp-icon-btn"
                                  aria-label={`עריכת ${food.foodName}`}
                                  title="עריכה"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    openEdit(food);
                                  }}
                                >
                                  <PencilSimple size={14} weight="fill" />
                                </button>
                                <button
                                  type="button"
                                  className="nfp-icon-btn danger"
                                  aria-label={`מחיקת ${food.foodName}`}
                                  title="מחיקה"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setFoodToDelete(food);
                                  }}
                                >
                                  <Trash size={14} weight="fill" />
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="nfp-footer">
                <span className="nfp-count">{foods.length} מזונות מוצגים</span>
                {data?.hasMore && (
                  <button
                    type="button"
                    className="nfp-loadmore"
                    onClick={handleLoadMore}
                    disabled={isFetching}
                  >
                    {isFetching ? (
                      <>
                        <LoaderLoop size="xs" />
                        <span>טוען...</span>
                      </>
                    ) : (
                      <span>טען עוד</span>
                    )}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {editorFood && (
        <FoodEditorModal
          mode={editorMode}
          initialForm={editorFood}
          isSaving={isCreating || isUpdating}
          onClose={() => setEditorFood(null)}
          onSubmit={handleSubmitFood}
        />
      )}

      {detailFood && (
        <FoodDetailModal
          food={detailFood}
          onClose={() => setDetailFood(null)}
          onEdit={() => {
            const food = detailFood;
            setDetailFood(null);
            openEdit(food);
          }}
          onDelete={() => setFoodToDelete(detailFood)}
        />
      )}

      <ConfirmDialog
        isOpen={!!foodToDelete}
        onClose={() => setFoodToDelete(null)}
        onConfirm={handleConfirmDelete}
        title="מחיקת מזון"
        message={`האם למחוק את "${foodToDelete?.foodName || ""}" ממאגר המרפאה?`}
        confirmText="מחיקה"
        cancelText="ביטול"
        type="danger"
        icon="delete"
        confirmDisabled={isDeleting}
      />
    </div>
  );
};

// ============================================================================
// Food editor modal (create / edit a clinic food)
// ============================================================================

const FoodEditorModal = ({ mode, initialForm, isSaving, onClose, onSubmit }) => {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [extrasOpen, setExtrasOpen] = useState(false);

  const setField = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const setExtra = (field, value) =>
    setForm((prev) => ({ ...prev, extras: { ...prev.extras, [field]: value } }));

  const addUnitRow = () =>
    setForm((prev) => ({ ...prev, units: [...prev.units, { unit: "", grams: "" }] }));

  const updateUnitRow = (index, patch) =>
    setForm((prev) => ({
      ...prev,
      units: prev.units.map((row, i) => (i === index ? { ...row, ...patch } : row)),
    }));

  const removeUnitRow = (index) =>
    setForm((prev) => ({
      ...prev,
      units: prev.units.filter((_, i) => i !== index),
    }));

  const handleSave = () => {
    const nextErrors = validateFoodForm(form);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    onSubmit(form);
  };

  const usedUnits = new Set(form.units.map((row) => row.unit).filter(Boolean));

  return (
    <div
      className="nfp-overlay"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="nfp-modal"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={mode === "edit" ? "עריכת מזון" : "הוספת מזון"}
      >
        <div className="nfp-modal-header">
          <div className="nfp-modal-title">
            <span className="nfp-title-icon">
              <BowlFood size={20} weight="fill" />
            </span>
            <div className="nfp-title-text">
              <span className="nfp-eyebrow">מאגר המזונות</span>
              <h2>{mode === "edit" ? "עריכת מזון" : "הוספת מזון"}</h2>
            </div>
          </div>
          <ModalCloseButton onClick={onClose} />
        </div>

        <div className="nfp-modal-body">
          <p className="nfp-note">
            כל הערכים מוזנים ל-100 גרם מזון. מזון חדש נשמר במאגר המרפאה ונשלח גם
            לצוות CliniKit לבדיקה לצורך הוספתו למאגר המשותף.
          </p>

          <Input
            label="שם המזון"
            name="foodName"
            value={form.foodName}
            onChange={(event) => setField("foodName", event.target.value)}
            placeholder="לדוגמה: גבינה לבנה 5%"
            error={errors.foodName}
            required
          />

          <div className="nfp-field-grid">
            {["calories", "carbohydrates", "protein", "fat"].map((field) => (
              <Input
                key={field}
                type="number"
                lang="en"
                min="0"
                step="0.1"
                label={`${NUTRIENT_LABELS[field].label} (${NUTRIENT_LABELS[field].unit})`}
                name={field}
                value={form[field]}
                onChange={(event) => setField(field, event.target.value)}
                error={errors[field]}
                required
              />
            ))}
          </div>

          {errors.macroSum && <p className="nfp-form-error">{errors.macroSum}</p>}

          {/* Serving units */}
          <div className="nfp-section">
            <div className="nfp-section-head">
              <span className="nfp-section-title">יחידות הגשה (אופציונלי)</span>
              <button type="button" className="nfp-inline-add" onClick={addUnitRow}>
                <Plus size={13} weight="bold" />
                <span>הוספת יחידה</span>
              </button>
            </div>

            {form.units.length === 0 ? (
              <p className="nfp-section-hint">
                ניתן להגדיר כמה גרמים שוקלת כף, כוס או יחידה אחת.
              </p>
            ) : (
              form.units.map((row, index) => (
                <div className="nfp-unit-row" key={`unit-${index}`}>
                  <select
                    className="nfp-select"
                    value={row.unit}
                    onChange={(event) => updateUnitRow(index, { unit: event.target.value })}
                    aria-label="יחידת מידה"
                  >
                    <option value="">בחרו יחידת מידה</option>
                    {CURATED_UNIT_LIST.map((unit) => (
                      <option
                        key={unit}
                        value={unit}
                        disabled={usedUnits.has(unit) && unit !== row.unit}
                      >
                        {unit}
                      </option>
                    ))}
                  </select>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    className="nfp-num-input nfp-unit-grams"
                    value={row.grams}
                    onChange={(event) => updateUnitRow(index, { grams: event.target.value })}
                    placeholder="גרם"
                    aria-label="משקל היחידה בגרמים"
                  />
                  <button
                    type="button"
                    className="nfp-icon-btn danger"
                    onClick={() => removeUnitRow(index)}
                    aria-label="הסרת יחידת מידה"
                  >
                    <Trash size={14} weight="fill" />
                  </button>
                  {errors[`unit_${index}`] && (
                    <p className="nfp-form-error nfp-unit-error">{errors[`unit_${index}`]}</p>
                  )}
                </div>
              ))
            )}
            {errors.unitsDuplicate && (
              <p className="nfp-form-error">{errors.unitsDuplicate}</p>
            )}
          </div>

          {/* Optional micronutrients */}
          <div className="nfp-section">
            <button
              type="button"
              className="nfp-collapse-btn"
              onClick={() => setExtrasOpen((open) => !open)}
              aria-expanded={extrasOpen}
            >
              <span>ערכים נוספים</span>
              <CaretDown
                size={13}
                weight="bold"
                className={`nfp-caret ${extrasOpen ? "open" : ""}`}
              />
            </button>

            {extrasOpen && (
              <div className="nfp-extras-grid">
                {OPTIONAL_FIELDS.map((field) => (
                  <label className="nfp-extra-field" key={field}>
                    <span className="nfp-extra-label">
                      {NUTRIENT_LABELS[field].label}
                      <span className="nfp-extra-unit"> ({NUTRIENT_LABELS[field].unit})</span>
                    </span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className={`nfp-num-input ${errors[`extra_${field}`] ? "invalid" : ""}`}
                      value={form.extras[field] ?? ""}
                      onChange={(event) => setExtra(field, event.target.value)}
                    />
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="nfp-modal-footer">
          <button type="button" className="nfp-secondary-btn" onClick={onClose}>
            ביטול
          </button>
          <button
            type="button"
            className="nfp-primary-btn"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <LoaderLoop size="xs" />
                <span>שומר...</span>
              </>
            ) : (
              <>
                <Check size={15} weight="bold" />
                <span>{mode === "edit" ? "שמירת שינויים" : "הוספת המזון"}</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Read-only detail modal
// ============================================================================

const FoodDetailModal = ({ food, onClose, onEdit, onDelete }) => {
  const isClinicFood = food.source === "clinic";
  const macros = ["calories", "carbohydrates", "protein", "fat"];
  const presentExtras = OPTIONAL_FIELDS.filter(
    (field) => food[field] !== undefined && food[field] !== null
  );
  const units = Object.entries(food.sizeOptions || {});

  return (
    <div className="nfp-overlay" onClick={onClose} role="presentation">
      <div
        className="nfp-modal"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={food.foodName}
      >
        <div className="nfp-modal-header">
          <div className="nfp-modal-title">
            <span className="nfp-title-icon">
              <BowlFood size={20} weight="fill" />
            </span>
            <div className="nfp-title-text">
              <span className="nfp-eyebrow">
                {isClinicFood ? "מזון המרפאה" : "מאגר כללי"}
              </span>
              <h2>{food.foodName}</h2>
            </div>
          </div>
          <ModalCloseButton onClick={onClose} />
        </div>

        <div className="nfp-modal-body">
          <p className="nfp-note">כל הערכים מוצגים ל-100 גרם מזון.</p>

          <div className="nfp-macro-tiles">
            {macros.map((field) => (
              <div className="nfp-macro-tile" key={field}>
                <span className="nfp-macro-value">{fmt(food[field])}</span>
                <span className="nfp-macro-label">
                  {NUTRIENT_LABELS[field].label} ({NUTRIENT_LABELS[field].unit})
                </span>
              </div>
            ))}
          </div>

          {units.length > 0 && (
            <div className="nfp-section">
              <span className="nfp-section-title">יחידות הגשה</span>
              <div className="nfp-units-list">
                {units.map(([unit, grams]) => (
                  <span className="nfp-badge nfp-badge-unit" key={unit}>
                    {unit} · {fmt(grams)} גרם
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="nfp-section">
            <span className="nfp-section-title">ערכים נוספים</span>
            {presentExtras.length === 0 ? (
              <p className="nfp-section-hint">לא הוזנו ערכים נוספים עבור מזון זה.</p>
            ) : (
              <div className="nfp-detail-rows">
                {presentExtras.map((field) => (
                  <div className="nfp-detail-row" key={field}>
                    <span className="nfp-detail-label">{NUTRIENT_LABELS[field].label}</span>
                    <span className="nfp-detail-value">
                      {fmt(food[field])} {NUTRIENT_LABELS[field].unit}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {isClinicFood && (
          <div className="nfp-modal-footer">
            <button type="button" className="nfp-danger-btn" onClick={onDelete}>
              <Trash size={15} weight="fill" />
              <span>מחיקה</span>
            </button>
            <button type="button" className="nfp-primary-btn" onClick={onEdit}>
              <PencilSimple size={15} weight="fill" />
              <span>עריכה</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FoodsPage;
